<!-- Template ottimizzato per slideshow TV 4K con pagination servizi -->
<template>
  <div class="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-purple-800 text-white font-sans">
    <!-- Header ottimizzato -->
    <div ref="headerControls"
      class="bg-black/20 backdrop-blur-sm px-8 py-6 flex justify-between items-center shadow-xl fixed top-0 left-0 right-0 z-50"
      :class="{
        'translate-y-0': showHeader || headerPinned,
        '-translate-y-full': !showHeader && !headerPinned
      }" 
      @mouseenter="showHeader = true" 
      @mouseleave="handleHeaderMouseLeave">
      
      <div class="flex items-center gap-6">
        <h1 class="text-4xl font-light">Monitor Pianificazioni</h1>
        <button @click="toggleHeaderPin"
          class="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-2"
          :class="{ 'bg-yellow-500/30 text-yellow-200': headerPinned }">
          <i :class="headerPinned ? 'fas fa-thumbtack' : 'fas fa-thumbtack'"
            :style="{ transform: headerPinned ? 'rotate(45deg)' : 'rotate(0deg)' }"></i>
          <span>{{ headerPinned ? 'Fissato' : 'Fissa' }}</span>
        </button>
      </div>

      <div class="flex items-center gap-6">
        <!-- Progress indicator -->
        <div v-if="monitorData.length > 0" class="flex items-center gap-4 bg-white/10 rounded-lg px-4 py-3">
          <span class="text-lg flex items-center gap-2">
            <i class="fas fa-map-marker-alt"></i>
            {{ currentComuneIndex + 1 }}/{{ monitorData.length }}
          </span>
          <div class="w-32 h-2 bg-white/20 rounded-full overflow-hidden">
            <div class="h-full transition-all duration-300 ease-linear"
              :class="isWaitingForPagination ? 'bg-orange-400' : 'bg-blue-400'"
              :style="{ width: `${progressPercentage}%` }"></div>
          </div>
          <span class="text-sm flex items-center gap-1">
            {{ remainingTime }}s
            <i v-if="isWaitingForPagination" class="fas fa-clock text-orange-400"></i>
          </span>
        </div>

        <!-- Week indicator -->
        <div class="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-3">
          <span class="text-lg flex items-center gap-2">
            <i class="fas fa-calendar-week"></i>
            {{ currentWeekIndex === 0 ? 'Settimana corrente' : 'Prossima settimana' }}
          </span>
        </div>

        <!-- Manual controls -->
        <div class="flex gap-2">
          <button @click="previousComune" class="px-4 py-3 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors">
            <i class="fas fa-chevron-left"></i>
          </button>
          <button @click="togglePause" 
            class="px-4 py-3 rounded-lg transition-colors"
            :class="isPaused ? 'bg-green-500 hover:bg-green-600' : 'bg-yellow-500 hover:bg-yellow-600'">
            <i :class="isPaused ? 'fas fa-play' : 'fas fa-pause'"></i>
          </button>
          <button @click="nextComune" class="px-4 py-3 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>

        <button @click="loadData" :disabled="loading"
          class="px-6 py-3 bg-green-500 hover:bg-green-600 disabled:opacity-60 rounded-lg transition-colors flex items-center gap-2">
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-sync-alt"></i>
          Aggiorna
        </button>
      </div>
    </div>

    <!-- Spacer per header fisso -->
    <div class="h-24"></div>

    <!-- Header reveal trigger -->
    <div class="fixed top-0 left-0 right-0 h-4 z-40" @mouseenter="showHeader = true"></div>

    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center justify-center h-96 gap-6">
      <div class="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
      <span class="text-2xl">Caricamento dati...</span>
    </div>

    <!-- Error -->
    <div v-if="error" class="mx-8 mt-8 text-center p-8 bg-red-500/20 border border-red-500/30 rounded-xl">
      <h3 class="text-2xl font-medium mb-4">Errore nel caricamento</h3>
      <p class="mb-6 text-red-200 text-lg">{{ error }}</p>
      <button @click="loadData" class="px-8 py-4 bg-red-500 hover:bg-red-600 rounded-lg transition-colors text-lg">
        Riprova
      </button>
    </div>

    <!-- No data -->
    <div v-if="!loading && !error && monitorData.length === 0" class="text-center py-20">
      <h3 class="text-3xl font-medium mb-6">Nessun dato disponibile</h3>
      <p class="text-xl">Non ci sono pianificazioni per il periodo selezionato.</p>
    </div>

    <!-- Main slideshow container -->
    <div v-if="!loading && !error && monitorData.length > 0" 
      class="px-8 pb-8" 
      style="height: calc(100vh - 200px);">
      
      <!-- Current comune display -->
      <div class="h-full flex flex-col">
        <!-- Comune header -->
        <div class="mb-8">
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-2xl border border-white/20">
            <div class="flex justify-between items-center">
              <h2 class="text-5xl font-light text-green-300 flex items-center gap-4">
                <i class="fas fa-map-marker-alt"></i>
                {{ currentComune?.comune }}
              </h2>
              <div class="flex items-center gap-6 text-xl">
                <span class="bg-white/10 rounded-full px-6 py-3 flex items-center gap-3">
                  <i class="fas fa-calendar-week"></i>
                  {{ getTotalGiorni(currentComune) }} giorni
                </span>
                <span class="bg-blue-500/30 rounded-full px-6 py-3 flex items-center gap-3">
                  <i class="fas fa-tasks"></i>
                  {{ getTotalServiziComune(currentComune) }} servizi
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Giorni grid - 6 colonne (senza domenica) -->
        <div class="flex-1 overflow-visible relative">
          <!-- Badges overlay - posizionati sopra la griglia -->
          <div class="absolute inset-0 pointer-events-none" style="z-index: 50;">
            <div class="grid grid-cols-6 gap-8 h-full">
              <div v-for="(giorno) in getWeekdaysOnly(currentComune?.giorni)" :key="giorno.data"
                class="relative">
                
                <!-- Badge "OGGI" -->
                <div v-if="giorno.isToday"
                  class="absolute -top-3 -right-3 bg-red-500 text-white text-sm font-bold px-3 py-2 rounded-full shadow-lg animate-pulse pointer-events-auto">
                  OGGI
                </div>

                <!-- Indicatore pagination attiva -->
                <div v-if="hasActivePagination(giorno.data)"
                  class="absolute -top-2 -left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1 pointer-events-auto">
                  <i class="fas fa-layers animate-pulse"></i>
                  {{ getCurrentPage(giorno.data) }}/{{ getTotalPages(giorno.data) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Griglia giorni -->
          <div class="grid grid-cols-6 gap-8 h-full">
            <div v-for="giorno in getWeekdaysOnly(currentComune?.giorni)" :key="giorno.data"
              class="flex flex-col rounded-xl p-6 border transition-colors relative"
              :class="getGiornoClasses(giorno)">

              <!-- Header giorno - tutto su una riga -->
              <div class="flex-shrink-0 mb-6 pb-4 border-b flex items-center justify-between"
                :class="giorno.isToday ? 'border-yellow-300/50' : 'border-white/20'">
                <div class="flex items-center gap-3">
                  <h3 class="text-3xl font-medium"
                    :class="giorno.isToday ? 'text-yellow-300' : 'text-blue-300'">
                    {{ giorno.nomeGiorno.toUpperCase() }}
                  </h3>
                  <span class="text-2xl opacity-80">{{ formatDateShort(giorno.data) }}</span>
                </div>
                <span class="text-base rounded-full px-3 py-1 flex items-center gap-1 whitespace-nowrap" 
                  :class="giorno.servizi.length > 0
                    ? (giorno.isToday ? 'bg-yellow-500/30 text-yellow-100' : 'bg-purple-500/30')
                    : 'bg-gray-500/30 text-gray-300'">
                  <i :class="giorno.servizi.length > 0 ? 'fas fa-list' : 'fas fa-calendar-times'"></i>
                  {{ giorno.servizi.length }}
                </span>
              </div>

              <!-- Servizi area - con pagination -->
              <div class="flex-1 overflow-hidden">
                <div v-if="giorno.servizi.length > 0" class="h-full flex flex-col">
                  
                  <!-- Servizi visibili (pagina corrente) -->
                  <div class="flex-1 space-y-4">
                    <div v-for="(servizio, index) in getVisibleServizi(giorno)" :key="index"
                      class="rounded-lg p-4 border-l-4 transition-all duration-200"
                      :class="getServizioClasses(servizio)">

                      <!-- Tipo servizio -->
                      <div class="font-semibold text-xl flex items-center gap-3 mb-3">
                        <i :class="getServizioIcon(servizio.tipoServizio)"></i>
                        {{ servizio.tipoServizio }}
                      </div>

                      <!-- Dettagli servizio -->
                      <div class="space-y-2 text-xl">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-2">
                            <i class="fas fa-user"></i>
                            <span>{{ servizio.operatore }}</span>
                          </div>
                          <div class="flex items-center gap-2">
                            <i class="fas fa-clock"></i>
                            <span>{{ servizio.oraInizio }}</span>
                            <span v-if="servizio.oraFine"> - {{ servizio.oraFine }}</span>
                          </div>
                        </div>

                        <div v-if="servizio.veicolo" class="flex items-center gap-2">
                          <i class="fas fa-truck"></i>
                          <span>{{ servizio.veicolo }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Indicatori pagination (se necessari) -->
                  <div v-if="needsPagination(giorno)" class="flex-shrink-0 mt-4 flex justify-center">
                    <div class="flex items-center gap-2">
                      <div v-for="pageNum in getTotalPages(giorno.data)" :key="pageNum"
                        class="w-2 h-2 rounded-full transition-colors"
                        :class="pageNum === getCurrentPage(giorno.data) ? 'bg-blue-400' : 'bg-white/30'">
                      </div>
                    </div>
                  </div>
                </div>

                <!-- No servizi message -->
                <div v-else class="h-full flex flex-col items-center justify-center opacity-60">
                  <i class="fas fa-calendar-times text-4xl mb-4"></i>
                  <span class="text-lg">Nessun servizio programmato</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="bg-black/20 backdrop-blur-sm px-8 py-4 text-center border-t border-white/20 fixed bottom-0 left-0 right-0">
      <div class="text-lg flex items-center justify-center gap-8">
        <span class="flex items-center gap-2">
          <i class="fas fa-info-circle"></i>
          Ultimo aggiornamento: {{ lastUpdate ? formatDateTime(lastUpdate) : 'Mai' }}
        </span>
        <span class="flex items-center gap-2 text-green-300">
          <i class="fas fa-play animate-pulse"></i>
          Slideshow attivo 
          <span v-if="isWaitingForPagination" class="text-orange-300">(attendendo pagination)</span>
          <span v-else>({{ config.SLIDESHOW_DURATION_SECONDS || 10 }}s per comune)</span>
        </span>
        <span class="flex items-center gap-2 text-purple-300">
          <i class="fas fa-calendar-week"></i>
          Ciclo settimane automatico
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import axios from 'axios'
import config from '@/config/config'

interface ServizioItem {
  tipoServizio: string
  operatore: string
  oraInizio: string
  oraFine: string
  veicolo?: string
}

interface GiornoData {
  data: string
  nomeGiorno: string
  isToday: boolean
  servizi: ServizioItem[]
}

interface ComuneData {
  comune: string
  giorni: GiornoData[]
}

// Reactive state
const loading = ref(false)
const error = ref<string | null>(null)
const monitorData = ref<ComuneData[]>([])
const lastUpdate = ref<Date | null>(null)

// Header state
const showHeader = ref(false)
const headerPinned = ref(false)

// Slideshow state
const currentComuneIndex = ref(0)
const currentWeekIndex = ref(0)
const isPaused = ref(false)
const slideshowTimer = ref<number | null>(null)
const remainingTime = ref(config.SLIDESHOW_DURATION_SECONDS || 10)
const countdownTimer = ref<number | null>(null)

// Pagination state
const SERVIZI_PER_PAGINA = config.SERVIZI_PER_PAGINA || 4
const currentPages = ref<Map<string, number>>(new Map()) // giornoData -> currentPage
const paginationTimers = ref<Map<string, number>>(new Map()) // giornoData -> timer
const isWaitingForPagination = ref(false)
const paginationCompleted = ref<Map<string, boolean>>(new Map()) // giornoData -> completed

// Date controls
const dataInizio = ref('')
const dataFine = ref('')

// API base URL
const API_BASE = config.API_BASE_URL || 'http://localhost:3454'

// Computed properties
const currentComune = computed(() => {
  return monitorData.value[currentComuneIndex.value] || null
})

const progressPercentage = computed(() => {
  if (isWaitingForPagination.value) {
    return 100 // Progress bar piena quando aspetta
  }
  const duration = config.SLIDESHOW_DURATION_SECONDS || 10
  return ((duration - remainingTime.value) / duration) * 100
})

// Week utility functions
function getWeekDates(weekOffset = 0): { start: Date, end: Date } {
  const today = new Date()
  const currentDay = today.getDay()

  let monday = new Date(today)
  if (currentDay === 0) {
    monday.setDate(today.getDate() + 1)
  } else {
    monday.setDate(today.getDate() - (currentDay - 1))
  }

  monday.setDate(monday.getDate() + (weekOffset * 7))
  monday.setHours(0, 0, 0, 0)

  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)

  return { start: monday, end: sunday }
}

function setWeekDates(weekOffset: number) {
  const { start, end } = getWeekDates(weekOffset)
  dataInizio.value = formatDateForInput(start)
  dataFine.value = formatDateForInput(end)
}

function formatDateForInput(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Data loading
async function loadData() {
  loading.value = true
  error.value = null

  try {
    const response = await axios.get(`${API_BASE}/api/pianificazioni/monitor`, {
      params: {
        dataInizio: dataInizio.value,
        dataFine: dataFine.value
      }
    })

    monitorData.value = response.data
    lastUpdate.value = new Date()
    currentComuneIndex.value = 0

    await nextTick()
    initializePagination()
  } catch (err: unknown) {
    console.error('Error loading data:', err)
    error.value = 'Errore nel caricamento dei dati'
  } finally {
    loading.value = false
  }
}

async function loadDataForWeekCheck(weekIndex: number): Promise<boolean> {
  const { start, end } = getWeekDates(weekIndex)
  const tempDataInizio = formatDateForInput(start)
  const tempDataFine = formatDateForInput(end)

  try {
    const response = await axios.get(`${API_BASE}/api/pianificazioni/monitor`, {
      params: {
        dataInizio: tempDataInizio,
        dataFine: tempDataFine
      }
    })

    if (response.data && response.data.length > 0) {
      monitorData.value = response.data
      lastUpdate.value = new Date()
      currentComuneIndex.value = 0
      await nextTick()
      initializePagination()
      return true
    }
    return false
  } catch (err: unknown) {
    console.error('Error loading week data:', err)
    return false
  }
}

// Pagination functions
function needsPagination(giorno: GiornoData): boolean {
  return giorno.servizi.length > SERVIZI_PER_PAGINA
}

function getTotalPages(giornoData: string): number {
  const giorno = getWeekdaysOnly(currentComune.value?.giorni)?.find(g => g.data === giornoData)
  if (!giorno) return 1
  return Math.ceil(giorno.servizi.length / SERVIZI_PER_PAGINA)
}

function getCurrentPage(giornoData: string): number {
  return currentPages.value.get(giornoData) || 1
}

function hasActivePagination(giornoData: string): boolean {
  return getTotalPages(giornoData) > 1
}

function getVisibleServizi(giorno: GiornoData): ServizioItem[] {
  const currentPage = getCurrentPage(giorno.data)
  const startIndex = (currentPage - 1) * SERVIZI_PER_PAGINA
  const endIndex = startIndex + SERVIZI_PER_PAGINA
  return giorno.servizi.slice(startIndex, endIndex)
}

function initializePagination() {
  stopAllPagination()
  currentPages.value.clear()
  paginationCompleted.value.clear()
  isWaitingForPagination.value = false

  if (!currentComune.value) return

  const weekdays = getWeekdaysOnly(currentComune.value.giorni)
  
  weekdays.forEach(giorno => {
    currentPages.value.set(giorno.data, 1)
    paginationCompleted.value.set(giorno.data, false)
    
    if (needsPagination(giorno)) {
      console.log(`Avvio pagination per ${giorno.nomeGiorno}: ${giorno.servizi.length} servizi, ${getTotalPages(giorno.data)} pagine`)
      startPaginationForGiorno(giorno.data)
    } else {
      paginationCompleted.value.set(giorno.data, true)
    }
  })
}

function startPaginationForGiorno(giornoData: string) {
  const totalPages = getTotalPages(giornoData)
  if (totalPages <= 1) {
    paginationCompleted.value.set(giornoData, true)
    return
  }

  let currentPage = 1
  const pageDisplayTime = config.PAGE_DISPLAY_TIME_MS || 3000 // Tempo per pagina
  
  const timer = setInterval(() => {
    if (isPaused.value) return
    
    currentPage++
    if (currentPage > totalPages) {
      // Completato un ciclo completo
      console.log(`Pagination completata per giorno: ${giornoData}`)
      paginationCompleted.value.set(giornoData, true)
      clearInterval(timer)
      paginationTimers.value.delete(giornoData)
      
      // Reset alla prima pagina
      currentPages.value.set(giornoData, 1)
    } else {
      currentPages.value.set(giornoData, currentPage)
    }
  }, pageDisplayTime)
  
  paginationTimers.value.set(giornoData, timer)
}

function stopAllPagination() {
  paginationTimers.value.forEach(timer => {
    clearInterval(timer)
  })
  paginationTimers.value.clear()
}

function areAllPaginationsCompleted(): boolean {
  if (!currentComune.value) return true
  
  const weekdays = getWeekdaysOnly(currentComune.value.giorni)
  return weekdays.every(giorno => 
    paginationCompleted.value.get(giorno.data) === true
  )
}

// Slideshow controls
function startSlideshow() {
  stopSlideshow()
  startCountdown()
  
  slideshowTimer.value = setInterval(async () => {
    if (!isPaused.value) {
      // Controlla se sono passati i 10 secondi base
      if (remainingTime.value <= 0) {
        // Controlla se tutte le pagination sono completate
        if (areAllPaginationsCompleted()) {
          await nextSlide()
        } else {
          // Attende il completamento delle pagination
          if (!isWaitingForPagination.value) {
            console.log('Attendendo completamento pagination...')
            isWaitingForPagination.value = true
          }
        }
      }
      
      // Se siamo in attesa, controlla ogni secondo se sono completate
      if (isWaitingForPagination.value && areAllPaginationsCompleted()) {
        console.log('Tutte le pagination completate, avanzando...')
        isWaitingForPagination.value = false
        await nextSlide()
      }
    }
  }, 1000)
}

function stopSlideshow() {
  if (slideshowTimer.value) {
    clearInterval(slideshowTimer.value)
    slideshowTimer.value = null
  }
  stopCountdown()
}

function startCountdown() {
  const duration = config.SLIDESHOW_DURATION_SECONDS || 10
  remainingTime.value = duration
  countdownTimer.value = setInterval(() => {
    if (!isPaused.value && !isWaitingForPagination.value) {
      remainingTime.value--
      if (remainingTime.value < 0) {
        remainingTime.value = 0
      }
    }
  }, 1000)
}

function stopCountdown() {
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value)
    countdownTimer.value = null
  }
}

async function nextSlide() {
  isWaitingForPagination.value = false
  
  if (currentComuneIndex.value < monitorData.value.length - 1) {
    currentComuneIndex.value++
  } else {
    // Fine dei comuni, prova settimana successiva
    const nextWeekIndex = currentWeekIndex.value === 0 ? 1 : 0
    setWeekDates(nextWeekIndex)
    
    const hasData = await loadDataForWeekCheck(nextWeekIndex)
    
    if (hasData) {
      currentWeekIndex.value = nextWeekIndex
    } else {
      // Torna alla settimana corrente
      setWeekDates(0)
      currentWeekIndex.value = 0
      await loadData()
    }
  }
  
  remainingTime.value = config.SLIDESHOW_DURATION_SECONDS || 10
  await nextTick()
  initializePagination()
}

function previousComune() {
  isWaitingForPagination.value = false
  
  if (currentComuneIndex.value > 0) {
    currentComuneIndex.value--
  } else {
    currentComuneIndex.value = monitorData.value.length - 1
  }
  remainingTime.value = config.SLIDESHOW_DURATION_SECONDS || 10
  nextTick(() => initializePagination())
}

function nextComune() {
  nextSlide()
}

function togglePause() {
  isPaused.value = !isPaused.value
}

// Utility functions
function getWeekdaysOnly(giorni: GiornoData[] | undefined): GiornoData[] {
  if (!giorni) return []
  
  // Filtra solo i giorni feriali (lunedì-sabato)
  return giorni.filter(giorno => {
    const date = new Date(giorno.data)
    const dayOfWeek = date.getDay()
    return dayOfWeek !== 0 // 0 = domenica
  })
}

function getTotalGiorni(comuneData: ComuneData | null): number {
  return getWeekdaysOnly(comuneData?.giorni).length
}

function getTotalServiziComune(comuneData: ComuneData | null): number {
  const weekdays = getWeekdaysOnly(comuneData?.giorni)
  return weekdays.reduce((total, giorno) => total + giorno.servizi.length, 0)
}

function formatDateShort(dateString: string): string {
  const date = new Date(dateString)
  return `${date.getDate()}/${String(date.getMonth() + 1).padStart(2, '0')}`
}

function formatDateTime(date: Date): string {
  return date.toLocaleString('it-IT')
}

function getGiornoClasses(giorno: GiornoData): string {
  const baseClasses = 'border'

  if (giorno.isToday) {
    return `${baseClasses} bg-yellow-500/20 border-yellow-400`
  } else if (giorno.servizi.length > 0) {
    return `${baseClasses} bg-white/10 border-white/20`
  } else {
    return `${baseClasses} bg-gray-600/20 border-gray-500/30`
  }
}

function getServizioClasses(servizio: ServizioItem): string {
  const tipo = servizio.tipoServizio.toLowerCase()

  if (tipo.includes('umido')) return 'bg-amber-600/20 border-amber-500'
  if (tipo.includes('secco')) return 'bg-gray-600/20 border-gray-500'
  if (tipo.includes('plastica')) return 'bg-yellow-600/20 border-yellow-500'
  if (tipo.includes('carta')) return 'bg-blue-600/20 border-blue-500'
  if (tipo.includes('vetro')) return 'bg-green-600/20 border-green-500'
  if (tipo.includes('verde')) return 'bg-emerald-600/20 border-emerald-500'
  if (tipo.includes('spazzamento')) return 'bg-orange-600/20 border-orange-500'
  if (tipo.includes('gestione') || tipo.includes('cdr')) return 'bg-purple-600/20 border-purple-500'

  return 'bg-slate-600/20 border-slate-500'
}

function getServizioIcon(tipoServizio: string): string {
  const tipo = tipoServizio.toLowerCase()

  if (tipo.includes('umido')) return 'fas fa-seedling'
  if (tipo.includes('secco')) return 'fas fa-trash-alt'
  if (tipo.includes('plastica')) return 'fas fa-recycle'
  if (tipo.includes('carta')) return 'fas fa-file-alt'
  if (tipo.includes('vetro')) return 'fas fa-wine-bottle'
  if (tipo.includes('verde')) return 'fas fa-leaf'
  if (tipo.includes('spazzamento')) return 'fas fa-broom'
  if (tipo.includes('gestione') || tipo.includes('cdr')) return 'fas fa-cogs'

  return 'fas fa-tools'
}

// Header management
function handleHeaderMouseLeave() {
  if (!headerPinned.value) {
    showHeader.value = false
  }
}

function toggleHeaderPin() {
  headerPinned.value = !headerPinned.value
}

// Lifecycle
onMounted(() => {
  setWeekDates(0)
  loadData().then(() => {
    if (monitorData.value.length > 0) {
      startSlideshow()
    }
  })
})

onUnmounted(() => {
  stopSlideshow()
  stopAllPagination()
})
</script>

<style scoped>
/* Optimizations for 4K TV */
* {
  box-sizing: border-box;
}

.font-sans {
  text-rendering: optimizeSpeed;
  font-kerning: none;
}

/* Smooth transitions for slideshow */
.transition-colors {
  transition: background-color 0.3s ease;
}

/* Reduced motion for better 4K performance */
@media (prefers-reduced-motion: reduce) {
  .transition-all,
  .transition-colors {
    transition: none !important;
  }
  
  .animate-spin,
  .animate-pulse {
    animation: none !important;
  }
}

/* Optimized grid layout for 6 columns (weekdays only) */
.grid-cols-6 {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

/* Header and footer positioning */
.fixed {
  position: fixed;
}

.translate-y-0 {
  transform: translateY(0);
}

.-translate-y-full {
  transform: translateY(-100%);
}

/* Performance optimizations */
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}

@supports not (backdrop-filter: blur(4px)) {
  .backdrop-blur-sm {
    backdrop-filter: none;
    background-color: rgba(0, 0, 0, 0.8);
  }
}

/* Hardware acceleration for smooth performance */
.transition-all {
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* Text optimization for large screens */
h1, h2, h3, h4, h5, h6 {
  text-rendering: optimizeSpeed;
  font-kerning: none;
}

/* Optimized animations */
@keyframes optimized-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes optimized-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.animate-spin {
  animation: optimized-spin 2s linear infinite;
}

.animate-pulse {
  animation: optimized-pulse 3s ease-in-out infinite;
}

/* Pagination indicators */
.pagination-dots {
  contain: layout style;
}

/* Smooth page transitions */
.space-y-4 > * {
  transition: opacity 0.3s ease;
}
</style>